/**
 * ============================================================================
 * GET DCR SERVICE ORDER SUMMARY API — accessor for POST
 * /getDcrServiceOrderSummary.
 * ============================================================================
 *
 * LAYER: accessor. Transport for the Dive Deep SO tab's grouped lifetime
 * table. Pages contain WHOLE DCR groups (a DCR's service orders are never
 * split across pages — model contract), so streaming appends never split a
 * group either. Cache policy lives in the RTK endpoint.
 */

import { ReportingApiClient } from 'src/accessors/ReportingApiClient';
import { followPaginationChain } from 'src/accessors/followPaginationChain';
import { SUMMARY_PAGE_SIZE } from 'src/accessors/GetDcrSummaryApi';
import {
  DcrServiceOrderGroup,
  GetDcrServiceOrderSummaryInput,
  GetDcrServiceOrderSummaryOutput,
} from 'src/types/lifetimeSummary.types';

class GetDcrServiceOrderSummaryApiClient extends ReportingApiClient {
  /** One page, exactly as the backend returns it. */
  public async getServiceOrderSummaryPage(
    input: GetDcrServiceOrderSummaryInput,
  ): Promise<GetDcrServiceOrderSummaryOutput> {
    return this.post<GetDcrServiceOrderSummaryOutput>('/getDcrServiceOrderSummary', input);
  }

  /**
   * Streams every page for the scope: onPage fires per page with that page's
   * complete DCR groups. `startKey` resumes a failed chain.
   */
  public async streamAllPages(
    input: GetDcrServiceOrderSummaryInput,
    onPage: (groups: DcrServiceOrderGroup[], pageNo: number, lastRefreshedAt: string) => void,
    startKey?: string,
  ): Promise<void> {
    const scoped: GetDcrServiceOrderSummaryInput = {
      ...input,
      pageSize: input.pageSize ?? SUMMARY_PAGE_SIZE,
    };
    await followPaginationChain(
      'GetDcrServiceOrderSummary',
      async (paginationKey) => {
        const page = await this.getServiceOrderSummaryPage(
          paginationKey ? { ...scoped, paginationKey } : scoped,
        );
        return { rows: page.data ?? [], paginationKey: page.paginationKey, lastRefreshedAt: page.lastRefreshedAt };
      },
      onPage,
      startKey,
    );
  }
}

/** Singleton (portal convention): one shared instance app-wide. */
export const GetDcrServiceOrderSummaryApi = new GetDcrServiceOrderSummaryApiClient();
